import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Warehouse, Plus, Search, MoreVertical, Trash2, Eye, RefreshCw, MapPin, Edit } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const GSE_STATUS = {
  operational: { label: 'Operational', color: 'bg-green-500/20 text-green-400' },
  maintenance: { label: 'Maintenance', color: 'bg-yellow-500/20 text-yellow-400' },
  unserviceable: { label: 'Unserviceable', color: 'bg-red-500/20 text-red-400' },
  retired: { label: 'Retired', color: 'bg-gray-500/20 text-gray-400' },
};

const GSE_CATEGORIES = [
  { value: 'tug', label: 'Aircraft Tug' },
  { value: 'fuel_truck', label: 'Fuel Truck' },
  { value: 'belt_loader', label: 'Belt Loader' },
  { value: 'cargo_loader', label: 'Cargo Loader' },
  { value: 'catering_truck', label: 'Catering Truck' },
  { value: 'lavatory_truck', label: 'Lavatory Truck' },
  { value: 'water_truck', label: 'Water Truck' },
  { value: 'gpu', label: 'Ground Power Unit (GPU)' },
  { value: 'acu', label: 'Air Conditioning Unit (ACU)' },
  { value: 'asu', label: 'Air Start Unit (ASU)' },
  { value: 'de_icing', label: 'De-icing Vehicle' },
  { value: 'passenger_stairs', label: 'Passenger Stairs' },
  { value: 'passenger_bus', label: 'Passenger Bus' },
  { value: 'jet_bridge', label: 'Jet Bridge' },
  { value: 'fire_truck', label: 'Fire Truck (ARFF)' },
  { value: 'maintenance_stand', label: 'Maintenance Stand' },
  { value: 'other', label: 'Other' },
];

export default function GSEPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { isDark } = useTheme();
  const [gseList, setGseList] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    equipment_id: '', name: '', category: 'tug', manufacturer: '', model: '',
    serial_number: '', status: 'operational', location: '', specific_location: '',
    owner_organization_id: '', operator_organization_id: '', last_inspection_date: '', next_inspection_due: ''
  });

  useEffect(() => {
    fetchGSE();
    fetchStats();
    fetchOrganizations();
  }, [api]);

  const fetchGSE = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterCategory !== 'all') params.append('category', filterCategory);
      const response = await api.get(`/gse?${params.toString()}`);
      setGseList(response.data);
    } catch (error) {
      toast.error('Failed to fetch GSE');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/gse/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/organizations?limit=200');
      setOrganizations(response.data);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      equipment_id: '', name: '', category: 'tug', manufacturer: '', model: '',
      serial_number: '', status: 'operational', location: '', specific_location: '',
      owner_organization_id: '', operator_organization_id: '', last_inspection_date: '', next_inspection_due: ''
    });
    setEditMode(false);
    setEditingId(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const handleCreateGSE = async () => {
    try {
      if (!formData.equipment_id || !formData.name || !formData.location) {
        toast.error('Please fill required fields');
        return;
      }
      
      if (editMode && editingId) {
        await api.put(`/gse/${editingId}`, formData);
        toast.success('GSE updated');
      } else {
        await api.post('/gse', formData);
        toast.success('GSE registered');
      }
      
      setShowCreateDialog(false);
      resetForm();
      fetchGSE();
      fetchStats();
    } catch (error) {
      toast.error(`Failed to ${editMode ? 'update' : 'create'} GSE`);
    }
  };

  const handleEditGSE = async (gse) => {
    try {
      const response = await api.get(`/gse/${gse.id}`);
      const data = response.data;
      
      setFormData({
        equipment_id: data.equipment_id || '',
        name: data.name || '',
        category: data.category || 'tug',
        manufacturer: data.manufacturer || '',
        model: data.model || '',
        serial_number: data.serial_number || '',
        status: data.status || 'operational',
        location: data.location || '',
        specific_location: data.specific_location || '',
        owner_organization_id: data.owner_organization_id || '',
        operator_organization_id: data.operator_organization_id || '',
        last_inspection_date: data.last_inspection_date || '',
        next_inspection_due: data.next_inspection_due || ''
      });
      
      setEditMode(true);
      setEditingId(gse.id);
      setShowCreateDialog(true);
    } catch (error) {
      toast.error('Failed to load GSE details');
    }
  };

  const getOrgsByType = (type) => {
    return organizations.filter(org => org.organization_types?.includes(type));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>Ground Support Equipment</h1>
          <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Manage tugs, GPUs, loaders, and other GSE</p>
        </div>
        <Button onClick={openCreateDialog} className="gap-2" data-testid="create-gse-btn">
          <Plus className="w-4 h-4" /> Add GSE
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>{stats.total || 0}</div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Total GSE</div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-500">{stats.operational || 0}</div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Operational</div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-sky-500">{Object.keys(stats.by_category || {}).length}</div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Categories</div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-purple-500">{Object.keys(stats.by_location || {}).length}</div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Locations</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Search GSE..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {GSE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchGSE}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? "border-slate-800" : ""}>
                <TableHead className={isDark ? "text-slate-400" : ""}>Equipment</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Category</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Location</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Owner</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8"><RefreshCw className="w-4 h-4 animate-spin inline" /> Loading...</TableCell></TableRow>
              ) : gseList.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8">No GSE found</TableCell></TableRow>
              ) : (
                gseList.map(gse => (
                  <TableRow key={gse.id} className={cn(isDark ? "border-slate-800 hover:bg-slate-800/50" : "hover:bg-gray-50")}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Warehouse className={cn("w-4 h-4", isDark ? "text-teal-400" : "text-teal-600")} />
                        <div>
                          <div className={cn("font-medium", isDark ? "text-white" : "")}>{gse.name}</div>
                          <div className={cn("text-xs font-mono", isDark ? "text-slate-400" : "text-gray-500")}>{gse.equipment_id}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{gse.category}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <MapPin className="w-3 h-3" />
                        <span className="font-mono">{gse.location}</span>
                      </div>
                      {gse.specific_location && <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>{gse.specific_location}</div>}
                    </TableCell>
                    <TableCell className={cn("text-sm", isDark ? "text-slate-300" : "")}>{gse.owner_organization_name || '-'}</TableCell>
                    <TableCell>
                      <Badge className={GSE_STATUS[gse.status]?.color}>{GSE_STATUS[gse.status]?.label || gse.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem><Eye className="w-4 h-4 mr-2" />View Details</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditGSE(gse)}><Edit className="w-4 h-4 mr-2" />Edit GSE</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-500"><Trash2 className="w-4 h-4 mr-2" />Retire</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateDialog(open); }}>
        <DialogContent className={cn("max-w-lg", isDark ? "bg-slate-900 border-slate-800" : "")}>
          <DialogHeader><DialogTitle className={isDark ? "text-white" : ""}>{editMode ? 'Edit GSE' : 'Register GSE'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Equipment ID *</Label>
                <Input value={formData.equipment_id} onChange={(e) => setFormData(p => ({...p, equipment_id: e.target.value}))} placeholder="TUG-001" className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData(p => ({...p, name: e.target.value}))} placeholder="Tug Alpha" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(p => ({...p, category: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{GSE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Manufacturer</Label>
                <Input value={formData.manufacturer} onChange={(e) => setFormData(p => ({...p, manufacturer: e.target.value}))} placeholder="TLD, JBT, etc." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Location (ICAO) *</Label>
                <Input value={formData.location} onChange={(e) => setFormData(p => ({...p, location: e.target.value.toUpperCase()}))} placeholder="OERK" className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Specific Location</Label>
                <Input value={formData.specific_location} onChange={(e) => setFormData(p => ({...p, specific_location: e.target.value}))} placeholder="Terminal 1, Gate 5" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? "text-slate-300" : ""}>Owner Organization</Label>
              <Select value={formData.owner_organization_id} onValueChange={(v) => setFormData(p => ({...p, owner_organization_id: v}))}>
                <SelectTrigger><SelectValue placeholder="Select owner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Operator Organization */}
            <div className="space-y-2">
              <Label className={isDark ? "text-slate-300" : ""}>Operator Organization</Label>
              <Select value={formData.operator_organization_id} onValueChange={(v) => setFormData(p => ({...p, operator_organization_id: v}))}>
                <SelectTrigger><SelectValue placeholder="Select operator" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {getOrgsByType('operator').map(org => <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>)}
                  {getOrgsByType('ground_handler').map(org => <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateGSE}>{editMode ? 'Update GSE' : 'Register GSE'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
